import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ShoppingBag, Users, Package, IndianRupee, AlertTriangle, Clock, TrendingUp, DollarSign, PiggyBank } from 'lucide-react';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [profit, setProfit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard').then(r => setData(r.data)).catch(() => {}),
      api.get('/admin/dashboard/profit').then(r => setProfit(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-full p-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const stats = [
    { label: 'Total Orders', value: data.total_orders, icon: ShoppingBag, color: 'text-blue-600 bg-blue-50' },
    { label: 'Active Farmers', value: data.active_farmers, icon: Users, color: 'text-green-600 bg-green-50' },
    { label: 'Total Products', value: data.total_products, icon: Package, color: 'text-purple-600 bg-purple-50' },
    { label: 'Revenue', value: `\u20B9${data.total_revenue?.toLocaleString()}`, icon: IndianRupee, color: 'text-amber-600 bg-amber-50' },
  ];

  const statusColors = { pending: 'bg-yellow-100 text-yellow-800', preparing: 'bg-blue-100 text-blue-800', packed: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800' };

  return (
    <div data-testid="admin-dashboard" className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-serif font-medium">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Overview of your marketplace</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <Card key={i} data-testid={`stat-card-${i}`} className="rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                  <p className="text-2xl font-semibold mt-1">{s.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${s.color}`}><s.icon className="h-5 w-5" /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Profit overview */}
      {profit && (
        <div data-testid="profit-overview" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="rounded-xl"><CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">Total Revenue</p><TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <p data-testid="profit-revenue" className="text-xl font-semibold">{'\u20B9'}{(profit.total_revenue || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-xl"><CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">Procurement Cost</p><DollarSign className="h-4 w-4 text-amber-600" />
            </div>
            <p data-testid="profit-procurement" className="text-xl font-semibold">{'\u20B9'}{(profit.procurement_cost || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-xl"><CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">Gross Profit</p><PiggyBank className="h-4 w-4 text-primary" />
            </div>
            <p data-testid="profit-gross" className={`text-xl font-semibold ${profit.gross_profit >= 0 ? 'text-primary' : 'text-destructive'}`}>{'\u20B9'}{(profit.gross_profit || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-xl"><CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">Avg Order Value</p><ShoppingBag className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-xl font-semibold">{'\u20B9'}{(profit.avg_order_value || 0).toLocaleString()}</p>
            <p className="text-[11px] text-muted-foreground">{profit.orders_this_month || 0} orders this month</p>
          </CardContent></Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock */}
        <Card className="rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Low Stock Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.low_stock?.length === 0 ? <p className="text-sm text-muted-foreground">All products are well stocked</p> : (
              <div className="space-y-3">
                {data.low_stock?.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.farmer_name}</p>
                    </div>
                    <Badge variant="destructive" className="rounded-full text-xs">{p.stock} kg left</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="rounded-xl">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" /> Recent Orders
              </CardTitle>
              <Link to="/admin/orders" className="text-xs text-primary hover:underline">View All</Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.recent_orders?.map((o, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{o.order_id}</p>
                    <p className="text-xs text-muted-foreground">{o.customer_name} &middot; {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{'\u20B9'}{o.total}</p>
                    <Badge className={`${statusColors[o.status]} border-0 rounded-full text-xs capitalize`}>{o.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
